import * as React from 'react';
import WritingStats, { WritingStatProps } from './Components/WritingStats';
import styled from 'styled-components';
import { EmotionsAndTopicsStats } from './Components/EmotionsAndTopicsStats';
import { TimePeriodPicker } from './Components/TimePeriodPicker';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import { getPagesHistory } from '../PagesHistory/selectors';
import {
  convertPeriod,
  currentWeeklyPeriod,
  formatPeriod,
  moveToNextPeriod,
  moveToPrevPeriod,
  Period,
  PeriodType,
} from './Models';
import SegmentedControl from '../../components/SegmentedControl';
import { isProFeaturesAvailable, SubscriptionState } from '../../Models';
import { getSubscriptionsState } from '../UserProfile/selectors';
import { getAuthToken } from '../Auth/selectors';
import IMPComponent from '../../components/interfaces/IMPComponent';
import { InjectedIntlProps, injectIntl } from 'react-intl';
import { RouteComponentProps } from 'react-router';
import { buildFakeInsightsReport } from './Logic/FakeReports';
import { TrialInfoComponent } from '../../components/TrialInfo';
import { AllScreenInfoWindow } from '../../components/AllScreenInfoWindow/AllScreenInfoWindow';
import InsightsInfo from '../../components/InsightsInfoComponent/InsightsInfo';
import { useUserNotice } from '../UserProfile/UserNoticeHook';
import { UserNotice } from '../UserProfile/reducer';
import { AllScreenSubscriptionsPromo } from '../../components/SubscriptionsPromo';
import { buildInsightsReport, writingTimeStringReport } from './Logic/Reports';
import { MoodInsights } from '../../components/MoodInsights';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 40px 5em;
`;

const WatsonStatsContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex-grow: 1;
  width: 100%;
  margin: 40px 0;

  > * {
    flex: 2 1 0;
    overflow: scroll;
  }

  > div:first-child {
    margin-right: 40px;
  }
`;

const NavigationContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

const PeriodSegmentedControl = styled(SegmentedControl)`
  width: 457px;
  height: 34px;
`;

interface AllScreenNoticeProps {
  hasPages: boolean;
  isLoggedIn?: boolean;
  readonly subscriptionState: SubscriptionState | null;
}

const AllScreenInsightsNotice: React.FunctionComponent<AllScreenNoticeProps> = props => {
  const userNoticeDidShow = useUserNotice(UserNotice.insightsPromoDidShow);

  const shouldShowUserNotice = !props.isLoggedIn || !props.hasPages || !userNoticeDidShow;

  if (shouldShowUserNotice) {
    return (
      <AllScreenInfoWindow>
        <InsightsInfo isLoggedIn={props.isLoggedIn} hasPages={props.hasPages} />
      </AllScreenInfoWindow>
    );
  }

  if (!isProFeaturesAvailable(props.subscriptionState)) {
    return <AllScreenSubscriptionsPromo />;
  }

  return null;
};

interface StatsProps extends IMPComponent, InjectedIntlProps {
  readonly pages: any;
  readonly subscriptionState: SubscriptionState | null;
  readonly isLoggedIn: boolean;
}

interface StatsState {
  readonly period: Period;
}

const enhance = compose<any, any>(
  injectIntl,
  connect(state => {
    const pages = getPagesHistory(state);
    const subscriptionState = getSubscriptionsState(state);
    return {
      pages,
      subscriptionState,
      isLoggedIn: Boolean(getAuthToken(state)),
    };
  }),
);

export class StatsComponent extends React.Component<StatsProps & RouteComponentProps & InjectedIntlProps, StatsState> {
  constructor(props: StatsProps & RouteComponentProps, context: any) {
    super(props, context);

    this.state = {
      period: currentWeeklyPeriod(),
    };
  }

  public render(): React.ReactNode {
    const {
      intl: { formatMessage },
    } = this.props;

    const messageBase = 'containers.Insights';

    const report = this.hasPages()
      ? buildInsightsReport(this.state.period, this.props.pages)
      : buildFakeInsightsReport(this.state.period);

    const stats: WritingStatProps[] = [
      {
        title: formatMessage({
          id: `${messageBase}.WritingStats.total_words`,
        }),
        stat: report.wordsReport.total ? `${report.wordsReport.total}` : `0`,
        change: report.wordsReport.diff,
      },
      {
        title: formatMessage({
          id: `${messageBase}.WritingStats.total_time`,
        }),
        stat: writingTimeStringReport(report.timeReport.total),
        change: report.timeReport.diff,
      },
      {
        title: formatMessage({
          id: `${messageBase}.WritingStats.total_pages`,
        }),
        stat:
          report.pagesReport.total === undefined ? '-' : report.pagesReport.total ? `${report.pagesReport.total}` : '0',
        change: report.pagesReport.diff,
      },
      {
        title: formatMessage({
          id: `${messageBase}.WritingStats.average_time`,
        }),
        stat: writingTimeStringReport(report.avgTimeReport.total),
        change: report.avgTimeReport.diff,
      },
    ];

    const segmentedOptions = [
      {
        label: formatMessage({
          id: `${messageBase}.segment.day`,
        }),
        value: 'day',
      },
      {
        label: formatMessage({
          id: `${messageBase}.segment.week`,
        }),
        value: 'week',
        default: true,
      },
      {
        label: formatMessage({
          id: `${messageBase}.segment.month`,
        }),
        value: 'month',
      },
      {
        label: formatMessage({
          id: `${messageBase}.segment.year`,
        }),
        value: 'year',
      },
    ];

    return (
      <Container>
        <AllScreenInsightsNotice
          hasPages={this.hasPages()}
          isLoggedIn={this.props.isLoggedIn}
          subscriptionState={this.props.subscriptionState}
        />
        <NavigationContainer>
          <PeriodSegmentedControl name="segmented-control" options={segmentedOptions} setValue={this.setPeriodType} />
          <TimePeriodPicker
            text={formatPeriod(report.period)}
            onNextClick={this.moveToNextPeriod}
            onPrevClick={this.moveToPrevPeriod}
          />
        </NavigationContainer>
        <WatsonStatsContainer>
          <MoodInsights values={report.moodReports} periodType={report.period.type} />
          <EmotionsAndTopicsStats
            emotions={report.emotionsReport.emotions}
            topics={report.emotionsReport.topics}
            failedStatuses={report.emotionsReport.failedStatuses}
          />
        </WatsonStatsContainer>
        <WritingStats stats={stats} />
        {this.props.subscriptionState && !this.props.subscriptionState.isSubscribed && (
          <TrialInfoComponent trialAnalysisLeft={this.props.subscriptionState!.trialAnalysisLeft} />
        )}
      </Container>
    );
  }

  private moveToNextPeriod = () => {
    this.setState({
      period: moveToNextPeriod(this.state.period),
    });
  };

  private moveToPrevPeriod = () => {
    this.setState({
      period: moveToPrevPeriod(this.state.period),
    });
  };

  private setPeriodType = (type: any) => {
    let nextPeriod = this.state.period;
    switch (type) {
      case 'day': {
        nextPeriod = convertPeriod(nextPeriod, PeriodType.DAY);
        break;
      }

      case 'week': {
        nextPeriod = convertPeriod(nextPeriod, PeriodType.WEEK);
        break;
      }

      case 'month': {
        nextPeriod = convertPeriod(nextPeriod, PeriodType.MONTH);
        break;
      }

      case 'year': {
        nextPeriod = convertPeriod(nextPeriod, PeriodType.YEAR);
        break;
      }

      case 'all-time': {
        nextPeriod = convertPeriod(nextPeriod, PeriodType.ALL);
        break;
      }
    }

    this.setState({
      period: nextPeriod,
    });
  };

  private hasPages = (): boolean => {
    return Object.keys(this.props.pages).length > 0;
  };
}

export const StatsContainer = enhance(StatsComponent);



// WEBPACK FOOTER //
// ./src/containers/Stats/index.tsx